import React from 'react';

/**
 * Simplified Cost Breakdown with Progress Bar
 *
 * Shows a visual progress bar for submission units vs budget.
 * - Green when within or at budget
 * - Red and overfills when over budget
 * - Shows influence cost when over budget
 */
export const CostBreakdown = ({ cost, budget, balance }) => {
  const { total: totalCost } = cost;
  const { base: baseBudget } = budget;

  const isOverBudget = totalCost > baseBudget;
  const overAmount = Math.max(0, totalCost - baseBudget);
  const influenceCost = overAmount; // 1 influence per extra unit
  const canAfford = balance >= influenceCost;

  // Progress percentage (capped at 100% for the bar, but we show overflow)
  const progressPercent = baseBudget > 0 ? Math.min(100, (totalCost / baseBudget) * 100) : 0;
  const overflowPercent = baseBudget > 0 ? Math.max(0, ((totalCost - baseBudget) / baseBudget) * 100) : 0;

  return (
    <div className="space-y-3">
      {/* Progress Bar */}
      <div className="space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Submission Size</span>
          <span className={`font-medium ${isOverBudget ? 'text-red-600' : 'text-gray-900'}`}>
            {totalCost} / {baseBudget} units
          </span>
        </div>

        <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
          {/* Base progress (green) */}
          <div
            className={`absolute left-0 top-0 h-full transition-all duration-300 ${
              isOverBudget ? 'bg-gray-400' : 'bg-green-500'
            }`}
            style={{ width: `${Math.min(progressPercent, 100)}%` }}
          />

          {/* Overflow (red) - extends beyond the bar */}
          {isOverBudget && (
            <div
              className="absolute top-0 h-full bg-red-500 transition-all duration-300"
              style={{
                left: '100%',
                width: `${Math.min(overflowPercent, 50)}%`,
                marginLeft: '-1px'
              }}
            />
          )}

          {/* Budget marker line */}
          <div className="absolute right-0 top-0 h-full w-0.5 bg-gray-600" />
        </div>
      </div>

      {/* Cost Summary */}
      <div className={`text-sm rounded-lg p-3 ${
        isOverBudget
          ? canAfford
            ? 'bg-amber-50 border border-amber-200'
            : 'bg-red-50 border border-red-200'
          : totalCost === 0
            ? 'bg-gray-50 border border-gray-200'
            : 'bg-green-50 border border-green-200'
      }`}>
        {totalCost === 0 ? (
          <p className="text-gray-600">Add content to your submission</p>
        ) : isOverBudget ? (
          <div className="space-y-1">
            <p className={canAfford ? 'text-amber-700' : 'text-red-700'}>
              <span className="font-semibold">{overAmount} extra unit{overAmount !== 1 ? 's' : ''}</span>
              {' '}&rarr;{' '}
              <span className="font-bold">{influenceCost} influence</span> to submit
            </p>
            {!canAfford && (
              <p className="text-red-600 text-xs">
                You only have {balance} influence. Remove content or earn more influence.
              </p>
            )}
          </div>
        ) : totalCost < baseBudget ? (
          <p className="text-green-700">
            <span className="font-semibold">{baseBudget - totalCost} free unit{baseBudget - totalCost !== 1 ? 's' : ''}</span> remaining
          </p>
        ) : (
          <p className="text-green-700">
            <span className="font-semibold">Perfect!</span> Using all {baseBudget} free units
          </p>
        )}
      </div>

      {/* Individual costs breakdown (compact) */}
      {totalCost > 0 && (
        <div className="flex flex-wrap gap-3 text-xs text-gray-500">
          {cost.text > 0 && (
            <span>Text: {cost.text} unit{cost.text !== 1 ? 's' : ''}</span>
          )}
          {cost.images > 0 && (
            <span>Images: {cost.images} unit{cost.images !== 1 ? 's' : ''}</span>
          )}
          {cost.audio > 0 && (
            <span>Audio: {cost.audio} unit{cost.audio !== 1 ? 's' : ''}</span>
          )}
        </div>
      )}
    </div>
  );
};
